import React, { useState, useEffect } from 'react';
import { Plus, FolderOpen, CheckCircle, FileText, RefreshCw, Settings, Calendar, Layers } from 'lucide-react';
import { Loading } from '../ui/Loading';
import { ProjectModal } from './ProjectModal';
import { projectService } from '../../services/api';
import { useDataCache } from '../../context/DataCacheContext';
import type { Project } from '../../types';

interface ProjectDashboardProps {
  onSelectProject: (project: Project) => void;
  onOpenScheduler: () => void;
}

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  onSelectProject,
  onOpenScheduler
}) => {
  const { projects, articlesByProject, articlesLoading, isLoading, refreshProjects, loadAllData, loadProjectArticles } = useDataCache();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    if (projects.length > 0) {
      const loadArticlesStaggered = async () => {
        for (let i = 0; i < Math.min(projects.length, 3); i++) {
          const project = projects[i];
          if (!articlesByProject[project.id]) {
            await loadProjectArticles(project.id);
          }
        }
        for (let i = 3; i < projects.length; i++) {
          const project = projects[i];
          if (!articlesByProject[project.id]) {
            setTimeout(() => loadProjectArticles(project.id), (i - 3) * 500);
          }
        }
      };
      loadArticlesStaggered();
    }
  }, [projects, articlesByProject, loadProjectArticles]);

  const [showProjectModal, setShowProjectModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  const handleRefresh = async () => {
    try {
      await refreshProjects();
    } catch (err: any) {
      setError(err.message || 'Error al recargar proyectos');
    }
  };

  const handleCreateProject = () => {
    setEditingProject(null);
    setModalMode('create');
    setShowProjectModal(true);
  };

  const handleEditProject = (project: Project, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingProject(project);
    setModalMode('edit');
    setShowProjectModal(true);
  };

  const handleSaveProject = async (projectData: Partial<Project>) => {
    try {
      const wpUrl = (projectData as any).url || undefined;
      const wpUsername = (projectData as any).username || undefined;
      const wpPassword = (projectData as any).password || undefined;
      const wpConnected = (projectData as any).wordpress_connected;

      if (modalMode === 'edit' && editingProject) {
        await projectService.updateProject(editingProject.id, {
          name: projectData.name,
          description: projectData.description,
          wordpress_url: wpUrl,
          wordpress_username: wpUsername,
          wordpress_password: wpPassword,
          wordpress_connected: wpConnected
        });
        alert('Proyecto actualizado correctamente');
      } else {
        await projectService.createProject(
          projectData.name || 'Nuevo Proyecto',
          projectData.description,
          undefined,
          wpUrl,
          wpUsername,
          wpPassword,
          wpConnected
        );
        alert('Proyecto creado correctamente');
      }

      await handleRefresh();
      setShowProjectModal(false);
      setEditingProject(null);
    } catch (err: any) {
      alert('Error al guardar el proyecto: ' + err.message);
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      return dateObj.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  // Loading state
  if (isLoading && projects.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading text="Cargando proyectos..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="max-w-md text-center bg-white/80 backdrop-blur-sm rounded-2xl p-10 shadow-sm border border-gray-200/50">
          <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-5">
            <FolderOpen className="w-7 h-7 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error al cargar</h3>
          <p className="text-gray-500 text-sm mb-6">{error}</p>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white text-sm rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const completedCount = projects.filter(p => p.status === 'completed').length;

  return (
    <div className="min-h-screen">
      {/* Scrollable Container */}
      <div className="h-[calc(100vh-64px)] overflow-y-auto overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">

          {/* Header */}
          <header className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  Mis Proyectos
                </h1>
                <p className="mt-1 text-sm text-gray-500">
                  {projects.length} proyecto{projects.length !== 1 ? 's' : ''} · {completedCount} completado{completedCount !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={onOpenScheduler}
                  className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  title="Programar contenidos"
                >
                  <Calendar className="w-5 h-5" />
                </button>
                <button
                  onClick={handleRefresh}
                  className="p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
                  title="Actualizar"
                >
                  <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={handleCreateProject}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Nuevo</span>
                </button>
              </div>
            </div>
          </header>

          {/* Projects Grid */}
          {projects.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-5">
                <Layers className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                Sin proyectos
              </h3>
              <p className="text-gray-500 text-sm mb-6 text-center max-w-xs">
                Crea tu primer proyecto para empezar
              </p>
              <button
                onClick={handleCreateProject}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Crear Proyecto
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((project) => {
                const articles = articlesByProject[project.id] || [];
                const isCompleted = project.status === 'completed';

                return (
                  <div
                    key={project.id}
                    onClick={() => onSelectProject(project)}
                    className="group relative bg-white rounded-2xl p-5 border border-gray-200 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-gray-300"
                  >
                    {/* Content */}
                    <div className="relative">
                      {/* Top Row: Avatar + Settings */}
                      <div className="flex items-start justify-between mb-4">
                        {/* Project Avatar */}
                        <div className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0 shadow-sm">
                          <span className="text-lg font-bold text-white">
                            {project.name ? project.name.charAt(0).toUpperCase() : '?'}
                          </span>
                        </div>

                        {/* Status + Settings */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isCompleted && (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                          )}
                          <button
                            onClick={(e) => handleEditProject(project, e)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Project Name & Description */}
                      <div className="mb-3">
                        <h3 className="text-base font-semibold text-gray-900 truncate mb-1">
                          {project.name}
                        </h3>
                        {project.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {project.description}
                          </p>
                        )}
                      </div>

                      {/* Bottom Row: Meta Info */}
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <FileText className="w-3.5 h-3.5" />
                          {articlesLoading[project.id] ? (
                            <span className="w-6 h-2.5 bg-gray-200 rounded animate-pulse" />
                          ) : (
                            articles.length
                          )}
                        </span>
                        {formatDate(project.createdAt) && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span>{formatDate(project.createdAt)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Project Modal */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => {
          setShowProjectModal(false);
          setEditingProject(null);
        }}
        onSave={handleSaveProject}
        project={editingProject}
        mode={modalMode}
      />
    </div>
  );
};
